import { useEffect, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { Info, ShieldCheck } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { AppearanceMenu } from "@/components/AppearanceMenu";
import { Logo } from "@/components/Logo";
import { LegalLinks } from "@/components/LegalLinks";
import { Loading, ErrorState } from "@/components/States";
import { PlanCard } from "@/components/pricing/PlanCard";
import { CompareTable } from "@/components/pricing/CompareTable";
import { CheckoutDialog } from "@/components/pricing/CheckoutDialog";
import { useAuth } from "@/context/AuthContext";
import { api, formatError } from "@/lib/api";
import { yearlySaving } from "@/lib/plan";

const ORDER = { free: 0, plus: 1, pro: 2 };

function ctaFor(p, user) {
  const learner = user && user.role === "learner";
  if (learner && user.plan === p.id && p.id !== "free" && user.plan_expires_at) return { label: user.plan_source === "trial" ? "Upgrade Now" : "Renew plan", disabled: false };
  if (learner && user.plan === p.id) return { label: "Current plan", disabled: true };
  if (learner && ORDER[user.plan] > ORDER[p.id]) return { label: "Included in your plan", disabled: true };
  return { label: p.id === "free" ? "Get Started" : "Upgrade Now", disabled: false };
}

function CurrencyToggle({ currency, onChange }) {
  return (
    <div data-testid="currency-toggle" className="inline-flex rounded-full border border-line bg-surface p-1 text-xs shadow-sm">
      {[["INR", "₹ India"], ["USD", "$ International"]].map(([k, l]) => (
        <button key={k} type="button" data-testid={`currency-${k}`} onClick={() => onChange(k)}
          className={`rounded-full px-3 py-1.5 font-semibold transition-colors ${currency === k ? "bg-aqua-solid text-white" : "text-ink-2 hover:text-ink"}`}>{l}</button>
      ))}
    </div>
  );
}

function CycleToggle({ cycle, setCycle, save }) {
  return (
    <div data-testid="billing-cycle-toggle" className="inline-flex rounded-full border border-line bg-surface p-1 shadow-sm">
      {[["monthly", "Monthly"], ["yearly", "Yearly"]].map(([k, l]) => (
        <button key={k} type="button" data-testid={`billing-cycle-${k}`} onClick={() => setCycle(k)}
          className={`rounded-full px-5 py-2 text-sm font-semibold transition-colors ${cycle === k ? "bg-solid text-white" : "text-ink-2 hover:text-ink"}`}>
          {l}{k === "yearly" && save > 0 && <span className={`ml-2 rounded-full px-2 py-0.5 text-[10px] ${cycle === k ? "bg-[var(--brand-cyan)] text-[#0B1220]" : "bg-aqua-soft text-aqua"}`}>Save up to {save}%</span>}
        </button>
      ))}
    </div>
  );
}

function Header({ user }) {
  return (
    <header className="sticky top-0 z-40 border-b border-line/80 bg-canvas/85 backdrop-blur-md">
      <div className="mx-auto flex max-w-7xl items-center justify-between px-4 py-3 sm:px-6 lg:px-8">
        <Logo className="h-9 sm:h-10" />
        <div className="flex items-center gap-2"><AppearanceMenu />
        <Button asChild className="rounded-full">
          {user ? <Link data-testid="pricing-dashboard-btn" to={user.role === "employer" ? "/employer" : "/dashboard"}>Go to dashboard</Link>
            : <Link data-testid="pricing-login-btn" to="/login">Log in</Link>}
        </Button></div>
      </div>
    </header>
  );
}

export default function Pricing() {
  const { user } = useAuth();
  const nav = useNavigate();
  const [data, setData] = useState(null);
  const [err, setErr] = useState("");
  const [cycle, setCycle] = useState("monthly");
  const [selected, setSelected] = useState(null);
  const [currency, setCurrency] = useState("INR");
  const locked = !!data?.country || !!user?.billing_currency;
  const changeCurrency = async (c) => {
    if (user?.role === "learner") {
      try { await api.post("/billing/region", { currency: c }); } catch (e) { return toast.error(formatError(e)); }
    }
    setCurrency(c);
  };
  const [params] = useSearchParams();
  useEffect(() => { api.get("/plans").then((r) => { setData(r.data); setCurrency(r.data.currency); }).catch((e) => setErr(formatError(e))); }, []);
  useEffect(() => {
    const want = params.get("upgrade");
    if (!data || !want || user === null) return;
    const p = data.plans.find((x) => x.id === want && x.id !== "free");
    if (!p) return;
    if (params.get("cycle") === "yearly") setCycle("yearly");
    if (!user) nav("/login", { state: { from: `/pricing?${params.toString()}` } });
    else if (user.role === "learner" && !ctaFor(p, user).disabled) setSelected(p);
  }, [data, user]); // eslint-disable-line react-hooks/exhaustive-deps

  const choose = async (p) => {
    if (p.id === "free") return nav(user ? "/dashboard" : "/register");
    if (!user) return nav(`/register?plan=${p.id}`);
    if (user.role !== "learner") return toast.info("Subscription plans apply to learner accounts.");
    setSelected(p);
  };

  if (user === null || (!data && !err)) return <Loading />;
  return (
    <div data-testid="pricing-page" className="min-h-screen bg-canvas">
      <Header user={user} />
      <main className="mx-auto max-w-7xl px-4 pb-24 pt-14 sm:px-6 lg:px-8 lg:pt-20">
        <div className="flex flex-col gap-8 lg:flex-row lg:items-end lg:justify-between">
          <div className="max-w-2xl">
            <p className="text-xs font-bold uppercase tracking-[0.2em] text-aqua">Plans & pricing</p>
            <h1 className="mt-4 text-4xl font-extrabold leading-[1.05] tracking-tight text-ink sm:text-5xl lg:text-6xl">Learn clinical research at your own pace.</h1>
            <p className="mt-5 text-base text-ink-2">Start free. Upgrade when you need more AI help, mock tests and interview preparation.</p>
          </div>
          {data && (
            <div className="flex flex-col items-start gap-3 lg:items-end">
              <CycleToggle cycle={cycle} setCycle={setCycle} save={Math.max(0, ...data.plans.map((p) => yearlySaving(p, currency)))} />
              {locked ? <p data-testid="currency-fixed" className="text-xs text-ink-3">Prices shown in {currency}{user?.billing_currency ? " (your billing currency)" : " for your region"}</p>
                : <CurrencyToggle currency={currency} onChange={changeCurrency} />}
            </div>
          )}
        </div>
        {err ? <div className="mt-12"><ErrorState message={err} /></div> : (
          <>
            <div className="mt-14 grid grid-cols-1 gap-6 md:grid-cols-3 lg:gap-8">
              {data.plans.map((p, i) => <PlanCard key={p.id} plan={p} index={i} cycle={cycle} currency={currency} cta={ctaFor(p, user)} busy={false} onClick={() => choose(p)} />)}
            </div>
            {!user && data.trial_days > 0 && (
              <p data-testid="trial-banner" className="mt-10 rounded-xl bg-band px-5 py-4 text-sm text-white">New to LearnAI? Every new account starts with a <b className="text-[var(--brand-cyan)]">{data.trial_days}-day free trial of LearnAI Plus</b>. No card needed. You move to Free automatically when it ends.</p>
            )}
            {data.payments_enabled ? (
              <p data-testid="payments-secure-note" className="mt-6 flex items-center gap-2 text-sm text-ink-2"><ShieldCheck className="h-4 w-4 text-aqua" />Secure one-time payments by Razorpay (UPI, cards, netbanking). No auto-renewal. We'll remind you before your plan ends.</p>
            ) : (
              <p data-testid="payments-disabled-note" className="mt-10 flex items-center gap-2 text-sm text-ink-2"><ShieldCheck className="h-4 w-4 text-aqua" />Online payments are coming soon. Choosing a paid plan records your interest only. You won't be charged and your plan stays the same.</p>
            )}
            <h2 className="mb-5 mt-16 font-display text-base font-bold text-ink md:text-lg">Compare plans</h2>
            <CompareTable plans={data.plans} subjectCount={data.subjects.length} currency={currency} />
            <div data-testid="pricing-notice" className="mt-8 flex gap-3 rounded-xl border border-amber-200 bg-amber-50/70 p-4 text-sm text-amber-900">
              <Info className="mt-0.5 h-4 w-4 shrink-0" /><p>{data.notice} By upgrading you agree to our <Link to="/legal/terms" className="font-semibold underline">Terms</Link> and <Link to="/legal/refunds" className="font-semibold underline">Refund & Cancellation Policy</Link>.</p>
            </div>
            <div className="mt-10 border-t border-line pt-6"><LegalLinks /></div>
          </>
        )}
      </main>
      <CheckoutDialog plan={selected} cycle={cycle} currency={currency} paymentsEnabled={!!data?.payments_enabled} testMode={data?.payments_mode === "test"} onClose={() => setSelected(null)} onDone={() => { setSelected(null); nav("/dashboard"); }} />
    </div>
  );
}
